// Этажи Tower — точка входа: конфиг, авторизация, экраны меты, кор-луп.
// Флоу: Splash → Карта уровней → Геймплей → Победа (подарок/share/дальше).
import * as THREE from 'three';
import { createEngine } from './scene.mjs';
import { createCrane } from './crane.mjs';
import { createHud } from './hud.mjs';
import { createGame } from './game.mjs';
import { createScreens } from './screens.mjs';
import { net } from './net.mjs';
import { setMuted, isMuted, getVolumes, setVolumes } from './audio.mjs';
import { starsForRun, perfectShare } from '../../lib/stars.js';
import { SKINS, setBlockSkin } from './block.mjs';
import { createQuality } from './quality.mjs';
import { createAchievements } from './achievements.mjs';

// Резервный конфиг на случай полного оффлайна (уровень 1, играбельный минимум)
const FALLBACK_CONFIG = {
  physics: {
    gravity: 22, perfectThreshold: 0.08, slipThreshold: 0.45, lives: 3, floorMeters: 3,
    sway: { omega: 2.1, damping: 0.55, impulsePerOffset: 0.06, maxTheta: 0.055, flexibleFloors: 7, perfectCalm: 0.45 },
    scoring: { perfect: 150, landingBase: 40, landingPrecisionBonus: 60, comboMultiplierFrom: 3, comboMultiplier: 2, goldenFloorBonus: 250, helicopterBonus: 500 },
  },
  levels: [{
    id: 1, name: 'Пятиэтажка', subtitle: 'Хрущёвка · двор детства', building: 'khrush', theme: 'yard', floors: 5,
    blockWidth: 3.4, blockHeight: 1.1, blockDepth: 3.4,
    pendulum: { amplitude: 0.48, speedStart: 0.95, speedPerFloor: 0.04, speedMax: 1.5, cable: 3.2 },
    wind: { fromFloor: 999, maxForce: 0 },
  }],
  rewards: [],
};

// Глобальные ловушки ошибок: без них падение у агента на телефоне — молчание.
// Дедуп по тексту — зацикленный rAF-краш не должен слать сотни репортов.
const reportedErrors = new Set();
function reportOnce(message, stack) {
  const key = String(message).slice(0, 120);
  if (reportedErrors.has(key) || reportedErrors.size > 10) return;
  reportedErrors.add(key);
  net.reportError(message, stack, location.href);
}
window.addEventListener('error', (e) => {
  reportOnce(e.message || 'window.onerror', e.error && e.error.stack);
});
window.addEventListener('unhandledrejection', (e) => {
  const r = e.reason || {};
  reportOnce(r.message || String(e.reason || 'unhandledrejection'), r.stack);
});

const PROGRESS_KEY = 'etazhi_tower_unlocked';
const DAILY_KEY = 'etazhi_tower_daily';
const tg = window.Telegram && window.Telegram.WebApp;
if (tg) {
  tg.ready();
  tg.expand();
}

async function boot() {
  const hud = createHud({
    // «Подобрали для тебя» после приза: подборка с сервера, интерес — в лид
    loadFeatured: () => net.featured(),
    onInterest: (objectId) => net.featuredInterest(objectId),
  });

  let config;
  try {
    config = await net.loadConfig();
  } catch (e) {
    console.warn('[tower] конфиг недоступен, играем на встроенном', e);
    config = FALLBACK_CONFIG;
  }

  // --- Авторизация и серверный прогресс (оффлайн-щит: не блокируем игру) ---
  const serverBest = {};       // level id → best score
  const serverStars = {};      // level id → звёзды (0..3), считает сервер
  const claimed = new Set();   // уровни с забранным призом
  const prevBestByLevel = {};  // рекорд ДО текущего забега — для «Рекорд · Сейчас»
  let lastRunStars = null;     // звёзды последнего забега — для анимации победы
  const authData = await net.auth(tg ? tg.initData : '');
  if (authData) {
    hud.authChip(authData.dev ? 'DEV-режим' : (authData.player.first_name || authData.player.username || 'игрок'));
    for (const p of authData.progress) { serverBest[p.level] = p.best_score; serverStars[p.level] = p.stars || 0; }
    for (const c of authData.claims) claimed.add(c.level);
    net.flushPending();
  } else {
    hud.authChip('офлайн');
  }

  // Разблокировка: максимум из localStorage и серверного прогресса
  function unlockedIndex() {
    const local = Number(localStorage.getItem(PROGRESS_KEY) || 0);
    const serverMax = Object.keys(serverBest).reduce((m, l) => Math.max(m, Number(l)), 0);
    return Math.min(Math.max(local, serverMax), config.levels.length - 1);
  }

  function rewardFor(levelId) {
    return (config.rewards || []).find((r) => r.levels.includes(levelId)) || null;
  }

  // --- Движок и сцена ---
  const canvas = document.getElementById('game-canvas');
  const { renderer, scene, camera, render, lights } = createEngine(canvas);
  const crane = createCrane();
  scene.add(crane.group);

  // Киношное зерно: маленькая шумовая плитка, тайлится на весь экран.
  // Слой прячет скейлер качества (ступень 3+) и ручной режим «Низкое».
  const grainEl = document.getElementById('grain');
  {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const g2d = c.getContext('2d');
    const img = g2d.createImageData(128, 128);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = 118 + Math.random() * 20 | 0;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    g2d.putImageData(img, 0, 0);
    grainEl.style.background = `url(${c.toDataURL()})`;
    grainEl.style.mixBlendMode = 'overlay';
  }

  // Авто-скейлер качества: держит FPS, снимая нагрузку ступенями
  const quality = createQuality({
    renderer,
    onChange(state) { grainEl.classList.toggle('off', !state.grain); },
  });

  // Виброотклик Telegram — заметная «дорогость» почти даром. Отключаемо.
  const HAPTIC_KEY = 'etazhi_tower_haptic';
  let hapticOn = localStorage.getItem(HAPTIC_KEY) !== '0';
  function haptic(kind) {
    const h = tg && tg.HapticFeedback;
    if (!h || !hapticOn) return;
    try {
      if (kind === 'success') h.notificationOccurred('success');
      else if (kind === 'heavy') h.impactOccurred('heavy');
      else if (kind === 'select') h.selectionChanged();
      else h.impactOccurred('light');
    } catch (e) { /* старый клиент Telegram — молча пропускаем */ }
  }
  // Каждая кнопка UI отбивается selectionChanged — консольное ощущение
  document.addEventListener('click', (e) => {
    if (e.target.closest && e.target.closest('button')) haptic('select');
  });

  // Тост: короткие сообщения о сети, повторные заменяют текущий
  const toastEl = document.getElementById('toast');
  let toastTimer = 0;
  function toast(msg, ms = 2600) {
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('show'), ms);
  }

  // --- Достижения: золотой тост при получении + экран со списком ---
  const achToastEl = document.getElementById('ach-toast');
  const achQueue = [];
  let achShowing = false;
  function showNextAch() {
    if (achShowing || !achQueue.length) return;
    achShowing = true;
    const def = achQueue.shift();
    // Контент — статичные константы achievements.mjs, пользовательских данных нет
    achToastEl.innerHTML = `${def.icon} Достижение: ${def.title}<span class="t">${def.desc}</span>`;
    achToastEl.classList.add('show');
    haptic('success');
    setTimeout(() => {
      achToastEl.classList.remove('show');
      setTimeout(() => { achShowing = false; showNextAch(); }, 400);
    }, 2800);
  }
  const achievements = createAchievements({
    onUnlock(def) { achQueue.push(def); showNextAch(); },
  });

  function renderAchievements() {
    document.getElementById('achv-count').textContent =
      `Открыто ${achievements.unlockedCount} из ${achievements.total}`;
    document.getElementById('achv-list').innerHTML = achievements.list().map((a) => `
      <div class="achv-row ${a.unlocked ? 'done' : 'locked'}">
        <div class="ico">${a.icon}</div>
        <div><div class="tt">${a.title}</div><div class="dd">${a.desc}</div></div>
      </div>`).join('');
  }
  // --- Башня дня: плашка на карте, загрузка уровня с сервера ---
  const dailyBanner = document.getElementById('daily-banner');
  let dailyCache = null; // { day, level } — чтобы не дёргать API на каждый клик
  async function refreshDaily() {
    try {
      const d = await net.daily();
      dailyCache = d;
      const my = d.my ? ` · твой лучший: ${d.my.score.toLocaleString('ru-RU')}` : '';
      document.getElementById('daily-sub').textContent =
        `${d.level.floors} эт. · ${d.level.subtitle}${my}`;
      dailyBanner.classList.remove('hidden');
    } catch (e) {
      dailyBanner.classList.add('hidden'); // оффлайн — плашку прячем, кампания работает
    }
  }
  document.getElementById('daily-play').addEventListener('click', () => {
    if (!dailyCache) return;
    screens.hideMap();
    game.loadDaily(dailyCache.level);
  });
  refreshDaily();
  // День сменился, пока апп открыт — плашка обновится при возврате на карту
  document.getElementById('btn-pause').addEventListener('click', () => {
    if (dailyCache && dailyCache.day !== new Date().toISOString().slice(0, 10)) refreshDaily();
  });

  // --- Сезонный ивент: баннер на карте + скин в подарок на время ивента ---
  // Всё приходит из /api/config (сервер читает config/events.json на лету);
  // приз за Башню дня выдаёт сервер сам — клиент только показывает тост.
  const activeEvent = config.event || null;
  if (activeEvent) {
    const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
      'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
    const [, m, d] = activeEvent.endsOn.split('-').map(Number);
    document.getElementById('event-name').textContent = `🎉 ${activeEvent.title}`;
    document.getElementById('event-sub').textContent =
      [activeEvent.subtitle, `по ${d} ${MONTHS[m - 1]}`].filter(Boolean).join(' · ');
    document.getElementById('event-banner').classList.remove('hidden');
  }

  // --- Скины стройки: косметика за суммарные звёзды, живёт в localStorage ---
  // Скин-подарок ивента открыт без звёзд, но только пока ивент идёт:
  // кончился — выбранный подарок откатывается на «Как есть».
  const SKIN_KEY = 'etazhi_tower_skin';
  const totalStars = () => Object.values(serverStars).reduce((a, b) => a + b, 0);
  const eventSkin = activeEvent ? activeEvent.skin : null;
  const skinOpen = (id) => {
    const s = SKINS.find((x) => x.id === id);
    return !!s && (totalStars() >= s.stars || id === eventSkin);
  };
  const currentSkin = () => {
    const saved = localStorage.getItem(SKIN_KEY) || 'default';
    return skinOpen(saved) ? saved : 'default';
  };
  setBlockSkin(currentSkin());
  function refreshSkins() {
    screens.renderSkins(SKINS, totalStars(), currentSkin(), (id) => {
      localStorage.setItem(SKIN_KEY, id);
      setBlockSkin(id);
      refreshSkins();
      toast(`🎨 Скин «${SKINS.find((s) => s.id === id).title}» — новые блоки в нём`);
    }, eventSkin);
  }
  document.getElementById('map-skins').addEventListener('click', () => {
    refreshSkins();
    screens.showSkins();
  });
  document.getElementById('skins-close').addEventListener('click', () => screens.hideSkins());

  document.getElementById('map-achv').addEventListener('click', () => {
    renderAchievements();
    document.getElementById('achv').classList.remove('hidden');
  });
  document.getElementById('achv-close').addEventListener('click', () => {
    document.getElementById('achv').classList.add('hidden');
  });

  // --- Экраны меты ---
  const screens = createScreens({
    onPlay(levelIndex) {
      game.loadLevel(levelIndex);
    },
    async onLeaderboard() {
      try {
        const data = await net.leaderboard();
        screens.showBoard({ top: data.top, weekly: data.weekly });
      } catch (e) {
        screens.showBoard({ top: [], weekly: [] });
      }
    },
  });

  function refreshMap() {
    screens.renderMap(config.levels, unlockedIndex(), serverBest, serverStars,
      (i) => game.loadForeman(i)); // каска «Прораб ×2» у уровней с 3⭐
  }

  // --- Забеги: runId для античита ---
  let runIdPromise = Promise.resolve(null);

  const urlLvl = Number(new URLSearchParams(location.search).get('lvl') || 0);
  const startLevel = urlLvl >= 1 && urlLvl <= config.levels.length
    ? urlLvl - 1
    : Math.min(unlockedIndex(), config.levels.length - 1);

  const game = createGame({
    scene, camera, crane, hud, lights, config, startLevel, haptic,
    achEvent: (name, payload) => achievements.event(name, payload),

    onLevelStart(levelId, isDaily, isForeman) {
      lastRunStars = null; // звёзды прошлого забега не должны утечь в новый оверлей
      runIdPromise = isDaily ? net.dailyStart() : net.levelStart(levelId, isForeman ? 'foreman' : undefined);
    },

    onWin(levelIndex) {
      const next = Math.min(levelIndex + 1, config.levels.length - 1);
      if (next > Number(localStorage.getItem(PROGRESS_KEY) || 0)) {
        localStorage.setItem(PROGRESS_KEY, String(next));
      }
    },

    async onLevelComplete(levelId, stats) {
      const runId = await runIdPromise;

      // Башня дня: свой зачёт + сразу говорим игроку его место
      if (stats.daily) {
        const r = await net.dailyComplete({ runId, ...stats });
        if (r && r.ok) {
          // Приз ивента важнее строчки рейтинга — если выдан, показываем его
          if (r.eventPrize) {
            haptic('success');
            toast(`🎉 ${r.eventPrize.title} — промокод уже у тебя в личке!`, 6000);
          } else {
            const mine = r.top.find((row) => row.you);
            if (mine) toast(`⚡ Ты №${mine.place} в Башне дня (${mine.score.toLocaleString('ru-RU')})`);
          }
        }
        return;
      }

      // Рекорд ДО забега — для строки «Рекорд · Сейчас» на экране победы
      prevBestByLevel[levelId] = serverBest[levelId] || 0;

      const result = await net.levelComplete({ runId, level: levelId, ...stats });
      if (result && result.ok) {
        for (const p of result.progress) { serverBest[p.level] = p.best_score; serverStars[p.level] = p.stars || 0; }
        if (!stats.foreman) lastRunStars = result.runStars ?? null;
      } else if (result === null) {
        // Оффлайн/сервер молчит: звёзды показываем по той же формуле локально
        // (в прогресс не попадут — сервер источник истины, это только экран)
        if (!stats.foreman && stats.floors === (config.levels.find((l) => l.id === levelId) || {}).floors) {
          const cfg = config.levels.find((l) => l.id === levelId);
          lastRunStars = starsForRun({
            floors: stats.floors, levelFloors: cfg.floors,
            perfects: stats.perfects, taps: stats.taps,
            share: perfectShare(cfg, config.physics),
          });
        }
        if (!navigator.onLine) toast('Нет связи — результат сохранится позже');
      }
    },

    // Подарочная карточка: приз уровня, если предусмотрен
    getGift(levelId) {
      const r = rewardFor(levelId);
      return r ? { title: r.title, claimed: claimed.has(levelId) } : null;
    },

    async claimGift(levelId) {
      const r = await net.claimReward(levelId);
      claimed.add(levelId);
      return r;
    },

    // Экран победы: рекорд до забега и звёзды, посчитанные сервером
    getBestScore: (levelId) => prevBestByLevel[levelId] || 0,
    getRunStars: () => lastRunStars,

    // Толпа зевак деградирует вместе с качеством рендера (1 → 0.5 → 0)
    getCrowdScale: () => quality.state.crowd,

    // Share: открытка (кадр игры + плашка с результатом) → страница с
    // OG-превью → шаринг в TG. Открытка выглядит постом, а не скриншотом.
    // Та же функция обслуживает кнопку «Снимок» фото-режима (shareRun ниже).
    onShare: shareRun,

    onMap() {
      hud.hideOverlay();
      refreshMap();
      screens.showMap();
    },

    // Ежедневный бонус: первая попытка дня стартует с +1 жизнью
    getBonusLives() {
      if (localStorage.getItem(DAILY_KEY) !== new Date().toDateString()) {
        localStorage.setItem(DAILY_KEY, new Date().toDateString());
        // Задержка, чтобы анонс уровня не перебил объявление бонуса
        setTimeout(() => hud.announce('Ежедневный бонус!', '+1 жизнь на эту попытку'), 2600);
        return 1;
      }
      return 0;
    },
  });

  async function shareRun(stats) {
      let pageUrl = location.origin;
      try {
        render(quality.state.bloom);
        const W = 900, HDR = 210, IMG = 990;
        const card = document.createElement('canvas');
        card.width = W;
        card.height = IMG + HDR;
        const ctx = card.getContext('2d');

        // Кадр игры: центральная вертикальная вырезка, cover-масштаб
        const srcAspect = canvas.width / canvas.height;
        const dstAspect = W / IMG;
        let sw = canvas.width, sh = canvas.height, sx = 0, sy = 0;
        if (srcAspect > dstAspect) { sw = canvas.height * dstAspect; sx = (canvas.width - sw) / 2; }
        else { sh = canvas.width / dstAspect; sy = (canvas.height - sh) / 2; }
        ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, W, IMG);

        // Плашка в фирменном красном
        ctx.fillStyle = '#e2231a';
        ctx.fillRect(0, IMG, W, HDR);
        ctx.fillStyle = '#ffffff';
        ctx.font = '900 52px -apple-system, "Segoe UI", Roboto, sans-serif';
        ctx.fillText('ЭТАЖИ TOWER', 40, IMG + 72);
        ctx.font = '700 34px -apple-system, "Segoe UI", Roboto, sans-serif';
        ctx.fillText(`${stats.level} · ${stats.floors} эт. · ${stats.score.toLocaleString('ru-RU')} очков`, 40, IMG + 126);
        ctx.font = '400 26px -apple-system, "Segoe UI", Roboto, sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,.85)';
        ctx.fillText('Строй выше. Живи лучше. Этажи.', 40, IMG + 172);

        const r = await net.share(card.toDataURL('image/png'));
        pageUrl = r.pageUrl;
      } catch (e) {
        console.warn('[share] снимок не ушёл, делимся ссылкой на игру', e);
      }
      const text = `Я построил «${stats.level}» в Этажи Tower — ${stats.floors} этажей и ${stats.score.toLocaleString('ru-RU')} очков! Сможешь выше? 🏗`;
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(text)}`;
      if (tg && tg.openTelegramLink) tg.openTelegramLink(shareUrl);
      else window.open(shareUrl, '_blank');
      achievements.event('share');
  }

  // Кнопки фото-режима: снимок уходит тем же share-флоу, что и «Поделиться»
  document.getElementById('photo-shot').addEventListener('click', () => shareRun(game.photoStats()));
  document.getElementById('photo-exit').addEventListener('click', () => game.exitPhoto());

  // --- Пауза и звук ---
  const pauseEl = document.getElementById('pause');
  const soundLabel = () => `Звук: ${isMuted() ? 'выкл' : 'вкл'}`;
  const soundIcon = () => (isMuted() ? '🔇' : '🔊');
  document.getElementById('btn-sound').textContent = soundIcon();
  document.getElementById('pause-sound').textContent = soundLabel();

  function openPause() {
    game.setPaused(true);
    pauseEl.classList.remove('hidden');
  }
  function closePause() {
    game.setPaused(false);
    pauseEl.classList.add('hidden');
  }
  document.getElementById('btn-pause').addEventListener('click', openPause);
  document.getElementById('pause-continue').addEventListener('click', closePause);
  document.getElementById('pause-restart').addEventListener('click', () => { closePause(); game.restart(); });
  document.getElementById('pause-map').addEventListener('click', () => { closePause(); refreshMap(); screens.showMap(); });
  document.getElementById('btn-sound').addEventListener('click', () => {
    setMuted(!isMuted());
    document.getElementById('btn-sound').textContent = soundIcon();
    document.getElementById('pause-sound').textContent = soundLabel();
  });
  document.getElementById('pause-sound').addEventListener('click', () => {
    setMuted(!isMuted());
    document.getElementById('btn-sound').textContent = soundIcon();
    document.getElementById('pause-sound').textContent = soundLabel();
  });

  // Микшер: музыка и эффекты раздельно, цикл 100 → 66 → 33 → 0
  const VOL_STEPS = [1, 0.66, 0.33, 0];
  function volCycle(cur) {
    const i = VOL_STEPS.findIndex((v) => Math.abs(v - cur) < 0.1);
    return VOL_STEPS[(i + 1) % VOL_STEPS.length];
  }
  const volBtn = (id, key, label) => {
    const btn = document.getElementById(id);
    const text = () => `${label}: ${Math.round(getVolumes()[key] * 100)}%`;
    btn.textContent = text();
    btn.addEventListener('click', () => {
      setVolumes({ [key]: volCycle(getVolumes()[key]) });
      btn.textContent = text();
    });
  };
  volBtn('pause-vol-music', 'music', 'Музыка');
  volBtn('pause-vol-sfx', 'sfx', 'Эффекты');

  // Вибрация: переключатель с памятью
  const hapticBtn = document.getElementById('pause-haptic');
  const hapticLabel = () => `Вибрация: ${hapticOn ? 'вкл' : 'выкл'}`;
  hapticBtn.textContent = hapticLabel();
  hapticBtn.addEventListener('click', () => {
    hapticOn = !hapticOn;
    localStorage.setItem(HAPTIC_KEY, hapticOn ? '1' : '0');
    hapticBtn.textContent = hapticLabel();
    if (hapticOn) haptic('light'); // мгновенный отклик — понятно, что включилось
  });

  // Качество: авто → высокое → низкое → авто
  const qualityBtn = document.getElementById('pause-quality');
  const QUALITY_LABELS = { auto: 'авто', high: 'высокое', low: 'низкое' };
  const qualityLabel = () => `Качество: ${QUALITY_LABELS[quality.mode]}`;
  qualityBtn.textContent = qualityLabel();
  qualityBtn.addEventListener('click', () => {
    const order = ['auto', 'high', 'low'];
    quality.setMode(order[(order.indexOf(quality.mode) + 1) % 3]);
    qualityBtn.textContent = qualityLabel();
  });

  // Сворачивание мини-аппа/вкладки — на паузу, чтобы не слить жизнь.
  // Возврат — вручную через «Продолжить»: игрок должен успеть собраться.
  document.addEventListener('visibilitychange', () => {
    const onScreens = ['splash', 'map', 'board', 'achv'].some(
      (id) => !document.getElementById(id).classList.contains('hidden')
    );
    if (document.hidden && !onScreens && !hud.isOverlayOpen && pauseEl.classList.contains('hidden')) {
      openPause();
    }
  });

  // --- Стартовый флоу: splash → карта ---
  refreshMap();
  screens.showSplash();

  // Управление: тап по игре (не по экранам/кнопкам) и пробел
  window.addEventListener('pointerdown', (e) => {
    if (e.target.closest && e.target.closest('.screen, #overlay, button')) return;
    game.drop();
  });
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Space') game.drop();
  });

  // Отладочный срез для автотестов Playwright
  window.__tower = game.debug;
  window.__towerInfo = () => renderer.info.render; // draw calls для замеров
  window.__quality = quality;
  window.__scene = scene;
  window.__towerDrop = () => game.drop();
  window.__towerRestart = () => game.restart();
  window.__towerLoadLevel = (i) => game.loadLevel(i);

  const clock = new THREE.Clock();
  let elapsed = 0;
  function tick() {
    requestAnimationFrame(tick);
    const dt = Math.min(clock.getDelta(), 1 / 30);
    elapsed += dt;
    game.update(dt, elapsed);
    render(quality.state.bloom);
    quality.update(renderer.info);
  }
  tick();
}

boot();
